import {
  _INTERNAL_captureSerializedMetric,
  _INTERNAL_flushMetricsBuffer,
  getClient as getSentryClient,
  type SerializedMetric,
  type SerializedMetricAttributeValue,
} from '@sentry/core'

export type BrowserMeasurementValue =
  | number
  | {
      value?: number
    }

export type BrowserTransactionEvent = {
  type?: string
  timestamp?: number
  transaction?: string
  contexts?: {
    trace?: {
      op?: string
      span_id?: string
      trace_id?: string
    }
  }
  transaction_info?: Record<string, unknown>
  measurements?: Record<string, BrowserMeasurementValue | undefined>
}

type TraceMetricDefinition = {
  name: string
  unit?: string
}

export const TRACE_METRIC_DEFINITIONS: Record<string, TraceMetricDefinition> = {
  cls: { name: 'cls' },
  fp: { name: 'fp', unit: 'millisecond' },
  fcp: { name: 'fcp', unit: 'millisecond' },
  inp: { name: 'inp', unit: 'millisecond' },
  lcp: { name: 'lcp', unit: 'millisecond' },
  ttfb: { name: 'ttfb', unit: 'millisecond' },
  // The installed Sentry browser utils emit this measurement as
  // `ttfb.requestTime`. Keep the lower-case variant as a fallback so we do not
  // silently drop the submetric if an older payload shape shows up.
  'ttfb.requestTime': {
    name: 'ttfb.requestTime',
    unit: 'millisecond',
  },
  'ttfb.requesttime': {
    name: 'ttfb.requestTime',
    unit: 'millisecond',
  },
} as const

export const readMeasurementValue = (
  measurement: BrowserMeasurementValue | undefined
) => {
  if (typeof measurement === 'number') {
    return measurement
  }

  return typeof measurement?.value === 'number' ? measurement.value : undefined
}

const serializeMetricAttribute = (
  value: string | number | boolean
): SerializedMetricAttributeValue => {
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? { value, type: 'integer' }
      : { value, type: 'double' }
  }

  if (typeof value === 'boolean') {
    return { value, type: 'boolean' }
  }

  return { value, type: 'string' }
}

const serializeMetricAttributes = (
  attributes: Record<string, string | number | boolean>
): Record<string, SerializedMetricAttributeValue> =>
  Object.fromEntries(
    Object.entries(attributes).map(([key, value]) => [
      key,
      serializeMetricAttribute(value),
    ])
  )

const scheduleMetricsFlush = () => {
  setTimeout(() => {
    const client = getSentryClient()
    if (client) {
      _INTERNAL_flushMetricsBuffer(client)
    }
  }, 0)
}

export const emitBrowserTraceMetrics = (event: BrowserTransactionEvent) => {
  if (event.type !== 'transaction') {
    return false
  }

  const traceContext = event.contexts?.trace
  const traceId = traceContext?.trace_id
  const spanId = traceContext?.span_id
  if (!traceId) {
    return false
  }

  const client = getSentryClient()
  if (!client) {
    return false
  }

  const transactionSource = event.transaction_info?.source
  const sharedAttributes: Record<string, string | number | boolean> = {}

  if (typeof event.transaction === 'string' && event.transaction) {
    sharedAttributes.transaction = event.transaction
    sharedAttributes['sentry.transaction'] = event.transaction
  }

  if (typeof traceContext?.op === 'string' && traceContext.op) {
    sharedAttributes['sentry.op'] = traceContext.op
  }

  if (typeof transactionSource === 'string' && transactionSource) {
    sharedAttributes['sentry.transaction.source'] = transactionSource
  }

  let capturedAny = false
  const metricTimestamp =
    typeof event.timestamp === 'number' && Number.isFinite(event.timestamp)
      ? event.timestamp
      : Date.now() / 1000

  for (const [measurementName, metric] of Object.entries(TRACE_METRIC_DEFINITIONS)) {
    const value = readMeasurementValue(event.measurements?.[measurementName])
    if (value === undefined || !Number.isFinite(value)) {
      continue
    }

    const serializedMetric: SerializedMetric = {
      timestamp: metricTimestamp,
      trace_id: traceId,
      name: metric.name,
      type: 'distribution',
      unit: metric.unit,
      value,
      attributes: {
        ...serializeMetricAttributes(sharedAttributes),
        'sentry.measurement_name': serializeMetricAttribute(measurementName),
      },
    }

    if (spanId) {
      serializedMetric.span_id = spanId
    }

    _INTERNAL_captureSerializedMetric(client, serializedMetric)
    capturedAny = true
  }

  if (capturedAny) {
    scheduleMetricsFlush()
  }

  return capturedAny
}
